import { useEffect, useState } from 'react'
import type { ErrorComponentProps } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Button } from '#/components/ui/button'
import {
  isModuleLoadError,
  shouldReloadForModuleError,
} from '#/lib/module-reload'
import { PageContainer } from '#/components/page-container'

function detailOf(error: unknown): string | null {
  if (error instanceof Error) return error.message
  return typeof error === 'string' ? error : null
}

export function RouteError({ error, reset }: ErrorComponentProps) {
  const { t } = useTranslation()
  // A failed dynamic import can't be recovered by reset() — it just re-renders the
  // same dead chunk URL. Reload to fetch the current module graph instead, but give
  // up after one attempt so a genuinely-missing chunk doesn't reload-loop.
  const recoverable = isModuleLoadError(error)
  // The error a reload was declined for, so each distinct error the boundary
  // surfaces (e.g. a retry that fails again) gets its own decision.
  const [gaveUpOn, setGaveUpOn] = useState<unknown>(null)

  useEffect(() => {
    if (!isModuleLoadError(error)) return
    if (shouldReloadForModuleError()) {
      window.location.reload()
      return
    }
    // The decision records the attempt in sessionStorage, so it can't run during render.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setGaveUpOn(error)
  }, [error])

  if (recoverable && gaveUpOn !== error) {
    return (
      <PageContainer>
        <p className="text-sm text-muted-foreground">{t('common.reloading')}</p>
      </PageContainer>
    )
  }

  const detail = detailOf(error)
  return (
    <PageContainer className="gap-4">
      <p className="text-sm text-destructive">{t('common.error')}</p>
      {detail ? (
        <p className="text-xs text-muted-foreground">{detail}</p>
      ) : null}
      <div>
        <Button type="button" variant="outline" onClick={reset}>
          {t('common.retry')}
        </Button>
      </div>
    </PageContainer>
  )
}
